import { useRouter } from 'vue-router';
import { useUser } from '@/store/user';
import { useUserConfig } from '@/store/user-config';
import { useOtherPlmBrands } from '@lib/brand-switch';
import { commandPalette } from '@lib/command-palette';
import { canSeeAdmin } from '@lib/admin-nav';
import { entitySearchKinds } from '@lib/palette-entity-search';
import { useMChartsLabel } from '@lib/mchart-label';
import { focusProductSearch } from '@lib/product-search';
import { openRouteInNewTab } from '@lib/router-new-tab';
import { shortcutHelp } from '@lib/shortcut-help';
import { useShortcut } from '@lib/shortcuts';

/** App-wide shortcuts, registered once from the frame. */
export function useGlobalShortcuts() {
  const router = useRouter();

  const enabled = () => useUser().loggedIn;

  useShortcut('mod+k', commandPalette.toggle, {
    enabled,
    allowInInput: true,
    description: 'Open command palette',
    scope: 'Global',
  });

  useShortcut('/', focusProductSearch, {
    enabled,
    description: 'Focus product search',
    scope: 'Global',
  });

  useShortcut('?', shortcutHelp.toggle, {
    enabled,
    description: 'Keyboard shortcuts',
    scope: 'Global',
  });

  // Go-to chords: `g` shows the destination list (chord-hint-popup), the next
  // letter navigates. Descriptions double as the popup's row labels. Each
  // destination also gets a shifted twin (`g P`) that opens a new tab; it stays
  // undescribed so it doesn't double the popup's rows — the hint explains it.
  const mChartsLabel = useMChartsLabel();
  const goTo = (
    key: string,
    description: string | (() => string),
    to: () => Parameters<typeof router.push>[0],
    gate?: () => boolean
  ) => {
    const isEnabled = () => enabled() && (gate?.() ?? true);
    useShortcut(`g ${key}`, () => router.push(to()), { enabled: isEnabled, description, scope: 'Go to' });
    useShortcut(`g shift+${key}`, () => openRouteInNewTab(router, to()), {
      enabled: isEnabled,
      scope: 'Go to',
      hint: 'Hold Shift to open in a new tab',
    });
  };

  goTo('p', 'Go to Products', () => ({
    name: 'products',
    query: { view: useUserConfig().settings?.products?.latestView },
  }));
  goTo('c', 'Go to Components', () => ({ name: 'components' }));
  goTo('s', 'Go to Samples', () => ({ name: 'samples' }));
  goTo('o', 'Go to Orders', () => ({ name: 'orders' }));
  goTo('u', 'Go to Suppliers', () => ({ name: 'suppliers' }));
  // 'l' because every other letter of "colors" already goes somewhere.
  goTo('l', 'Go to Colors', () => ({ name: 'colors' }));
  goTo(
    'm',
    () => `Go to ${mChartsLabel.value}`,
    () => ({ name: 'measurement-charts' })
  );
  goTo('a', 'Go to Admin', () => ({ name: 'admin' }), canSeeAdmin);

  // Search chords mirror the go-to ones: `s` shows the list, the next letter opens
  // the palette already inside that entity's search.
  entitySearchKinds.forEach((kind) => {
    useShortcut(kind.shortcut, () => commandPalette.openAt({ id: 'search', entity: kind.type }), {
      enabled,
      description: kind.label,
      scope: 'Search',
    });
  });

  const otherBrands = useOtherPlmBrands();
  useShortcut('s b', () => commandPalette.openAt({ id: 'brands' }), {
    enabled: () => enabled() && otherBrands.value.length > 0,
    description: 'Search for brands',
    scope: 'Search',
  });
}
